import {
  Button,
  type ButtonProps,
  IconButton,
  type IconButtonProps,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import type { ComponentType, ElementType } from 'react';
import { NavLink } from 'react-router-dom';
import { useI18n } from '../../shared/i18n';
import { TranslatedText } from '../../shared/ui/TranslatedText';
import { getNavigationRoutes } from './appNavigation';
import { footerContent } from './footerContent';
import {
  InstallIcon,
  MailIcon,
  PhoneIcon,
  TelegramIcon,
  WhatsappIcon,
} from './icons';

interface AppFooterProps {
  updatedAt: string;
  installDisabled: boolean;
  onInstall: () => Promise<void>;
  user: { id: string } | null;
}

const Footer = styled('footer')(({ theme }) => ({
  marginTop: theme.spacing(6),
  paddingTop: theme.spacing(3),
  borderTop: `1px solid ${theme.vars!.palette.surface.panelBorder}`,
}));

const FooterGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(3),
  gridTemplateColumns: '1.4fr 1fr 1.2fr',
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
  },
}));

const FooterColumn = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(3),
}));

const FooterLinks = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(1),
}));

const FooterPageLink = styled(NavLink)(({ theme }) => ({
  color: theme.vars!.palette.text.primary,
  textDecoration: 'none',
  width: 'fit-content',
  '&:hover': {
    textDecoration: 'underline',
  },
  '&.active': {
    color: theme.vars!.palette.primary.main,
    fontWeight: 700,
  },
}));

const FooterTextLink = styled(Link)(({ theme }) => ({
  color: theme.vars!.palette.text.primary,
  textDecoration: 'none',
  width: 'fit-content',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

const InlineFooterText = styled(TranslatedText)({
  display: 'inline',
});

const FooterInstallButton = styled(IconButton)(({ theme }) => ({
  color: theme.vars!.palette.text.primary,
}));

interface DonateButtonProps extends ButtonProps {
  component?: ElementType;
  end?: boolean;
  to?: string;
}

const DonateButton = styled(Button)(({ theme }) => ({
  alignSelf: 'flex-start',
  paddingInline: theme.spacing(2.25),
})) as ComponentType<DonateButtonProps>;

const ContactRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

interface ContactIconButtonProps extends IconButtonProps {
  component?: 'a';
  href?: string;
}

const ContactIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.vars!.palette.text.primary,
})) as ComponentType<ContactIconButtonProps>;

export function AppFooter({
  updatedAt,
  installDisabled,
  onInstall,
  user,
}: AppFooterProps) {
  const { t } = useI18n();
  const footerRoutes = getNavigationRoutes('footer', Boolean(user));
  const donateRoute = footerRoutes.find((route) => route.path === '/donate');
  const sitemapRoutes = footerRoutes.filter(
    (route) => route.path !== '/donate'
  );
  const handleInstallClick = () => {
    void onInstall();
  };

  const socialLinks = {
    telegram: {
      icon: <TelegramIcon />,
      label: t('footer.telegram'),
    },
    whatsapp: {
      icon: <WhatsappIcon />,
      label: t('footer.whatsapp'),
    },
  } as const;

  const directContactIcons = {
    phone: <PhoneIcon />,
    email: <MailIcon />,
  } as const;

  const hasContacts =
    footerContent.socialLinks.length > 0 ||
    footerContent.directContacts.length > 0;

  return (
    <Footer>
      <FooterGrid>
        <Stack spacing={1.5}>
          <TranslatedText messageKey="app.title" variant="h6" />
          <Typography color="text.secondary">
            <InlineFooterText
              component="span"
              variant="inherit"
              messageKey="footer.updated"
            />
            {`: ${updatedAt}`}
          </Typography>
          {/* FIXME: тултип должен оборачивать кнопку на не span */}
          {/* REFACTOR: нужно вынести в фичи, и переиспользовать в хедере */}
          <Tooltip title={t('app.actions.install')}>
            <span>
              <FooterInstallButton
                onClick={handleInstallClick}
                disabled={installDisabled}
              >
                <InstallIcon />
              </FooterInstallButton>
            </span>
          </Tooltip>
        </Stack>

        <FooterLinks>
          <TranslatedText
            messageKey="footer.linksTitle"
            variant="subtitle2"
            color="text.secondary"
          />
          {sitemapRoutes.map(({ path, labelKey, navMatchEnd }) => (
            <FooterPageLink key={path} to={path} end={navMatchEnd}>
              {labelKey ? t(labelKey) : path}
            </FooterPageLink>
          ))}
        </FooterLinks>

        {donateRoute ? (
          <FooterLinks>
            <TranslatedText
              messageKey="footer.donateSectionTitle"
              variant="subtitle2"
              color="text.secondary"
            />
            <DonateButton
              component={NavLink}
              to={donateRoute.path}
              end={donateRoute.navMatchEnd}
              variant="outlined"
              color="primary"
            >
              {donateRoute.labelKey
                ? t(donateRoute.labelKey)
                : donateRoute.path}
            </DonateButton>
          </FooterLinks>
        ) : null}

        <FooterColumn>
          <FooterLinks>
            <TranslatedText
              messageKey="footer.contactsTitle"
              variant="subtitle2"
              color="text.secondary"
            />
            {hasContacts ? (
              <>
                {footerContent.socialLinks.length > 0 ? (
                  <Stack spacing={1}>
                    {footerContent.socialLinks.map((link) => (
                      <ContactRow
                        key={`${link.key}-${link.href}`}
                      >
                        <ContactIconButton
                          component="a"
                          href={link.href}
                          aria-label={socialLinks[link.key].label}
                        >
                          {socialLinks[link.key].icon}
                        </ContactIconButton>
                        <FooterTextLink href={link.href} underline="none">
                          {link.value}
                        </FooterTextLink>
                      </ContactRow>
                    ))}
                  </Stack>
                ) : null}
                {footerContent.directContacts.map((contact) => (
                  <ContactRow
                    key={`${contact.key}-${contact.value}`}
                  >
                    <ContactIconButton
                      component="a"
                      href={contact.href}
                      aria-label={contact.value}
                    >
                      {directContactIcons[contact.key]}
                    </ContactIconButton>
                    <FooterTextLink href={contact.href} underline="none">
                      {contact.value}
                    </FooterTextLink>
                  </ContactRow>
                ))}
              </>
            ) : (
              <TranslatedText messageKey="footer.contactsPending" color="text.secondary" />
            )}
          </FooterLinks>
        </FooterColumn>
      </FooterGrid>
    </Footer>
  );
}
